import { useEffect } from "react";
import { LandingNav } from "./LandingNav";
import { LandingFooter } from "./LandingFooter";
import { STATUS_CONFIG, type RoadmapStatus } from "../data/roadmap-data";
import { usePageMeta } from "../../../hooks/usePageMeta";
import { useRoadmapItems, useToggleRoadmapVote, type RoadmapItemResponse } from "../../../lib/api/roadmap.api";
import { hasToken } from "../../../lib/api/client";

interface RoadmapPageProps {
  readonly isDark: boolean;
  readonly onToggleTheme: () => void;
}

function VoteButton({ item }: { readonly item: RoadmapItemResponse }) {
  const toggleVote = useToggleRoadmapVote();
  const isLoggedIn = hasToken();

  if (!isLoggedIn) {
    return (
      <a
        href="/login"
        className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs text-ink-faint hover:text-ink-muted transition-colors shrink-0"
        title="Connectez-vous pour voter"
      >
        <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2}>
          <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
        </svg>
        {item.voteCount > 0 && <span>{item.voteCount}</span>}
      </a>
    );
  }

  return (
    <button
      type="button"
      onClick={() => toggleVote.mutate(item.id)}
      disabled={toggleVote.isPending}
      className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs transition-colors shrink-0 ${
        item.hasVoted
          ? "bg-accent/10 text-accent"
          : "text-ink-muted hover:text-ink hover:bg-surface-alt"
      }`}
      title={item.hasVoted ? "Retirer mon vote" : "Voter pour cette fonctionnalité"}
    >
      <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill={item.hasVoted ? "currentColor" : "none"} stroke="currentColor" strokeWidth={2}>
        <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
      </svg>
      {item.voteCount > 0 && <span>{item.voteCount}</span>}
    </button>
  );
}

export function RoadmapPage({ isDark, onToggleTheme }: RoadmapPageProps) {
  usePageMeta(
    "Roadmap",
    "Découvrez les fonctionnalités en cours et prévues pour Palnia : intégrations, app mobile, mode hors-ligne et plus encore."
  );
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const { data: items, isLoading } = useRoadmapItems();

  const groups: { status: RoadmapStatus; items: RoadmapItemResponse[] }[] = [
    { status: "in-progress", items: items?.filter((i) => i.status === "in-progress") ?? [] },
    { status: "planned", items: items?.filter((i) => i.status === "planned") ?? [] },
    { status: "done", items: items?.filter((i) => i.status === "done") ?? [] },
  ];

  return (
    <div className="min-h-screen bg-surface text-ink antialiased flex flex-col">
      <LandingNav isDark={isDark} onToggleTheme={onToggleTheme} />

      <main className="flex-1 max-w-3xl mx-auto px-6 pt-24 pb-16 w-full">
        <div className="mb-10">
          <h1 className="text-2xl font-bold text-ink">Roadmap</h1>
          <p className="text-sm text-ink-muted mt-1">
            Ce qui arrive, ce qui est en cours, et ce qui est déjà fait.
            {hasToken()
              ? " Votez pour les fonctionnalités que vous attendez le plus."
              : " Connectez-vous pour voter et influencer les prochaines fonctionnalités."}
          </p>
        </div>

        {isLoading ? (
          <p className="text-sm text-ink-muted">Chargement...</p>
        ) : (
          <>
            <div className="flex items-center gap-6 mb-8">
              {(["in-progress", "planned", "done"] as RoadmapStatus[]).map((s) => {
                const config = STATUS_CONFIG[s];
                const count = items?.filter((i) => i.status === s).length ?? 0;
                return (
                  <div key={s} className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${config.dot}`} />
                    <span className={`text-xs font-medium ${config.color}`}>
                      {config.label} ({count})
                    </span>
                  </div>
                );
              })}
            </div>

            <div className="flex flex-col gap-10">
              {groups.map(({ status, items: groupItems }) => {
                const config = STATUS_CONFIG[status];
                return (
                  <section key={status}>
                    <h2 className={`text-lg font-semibold mb-4 ${config.color}`}>
                      {config.label}
                    </h2>
                    <div className="grid gap-3">
                      {groupItems.map((item) => (
                        <div
                          key={item.id}
                          className={`flex items-start gap-3 p-4 rounded-xl border transition-colors ${
                            status === "done"
                              ? "border-emerald-500/15 bg-emerald-500/5"
                              : status === "in-progress"
                                ? "border-amber-500/15 bg-amber-500/5"
                                : "border-border bg-surface-alt/50"
                          }`}
                        >
                          <span className={`shrink-0 mt-1.5 w-2 h-2 rounded-full ${config.dot}`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-ink">{item.title}</span>
                              <span className="text-[10px] text-ink-faint bg-surface-alt px-1.5 py-0.5 rounded">
                                {item.category}
                              </span>
                            </div>
                            <p className="text-xs text-ink-muted mt-0.5">{item.description}</p>
                          </div>
                          <VoteButton item={item} />
                        </div>
                      ))}
                    </div>
                  </section>
                );
              })}
            </div>
          </>
        )}
      </main>

      <LandingFooter />
    </div>
  );
}
